import { formatForecastMonth } from './ForecastGrid.js';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const currencyCents = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function money(value: number, withCents = false): string {
  return (withCents ? currencyCents : currency).format(value);
}

/** One forecast area feeding the combined totals (shares the months/total shape). */
export interface ForecastSummaryArea {
  key: string;
  label: string;
  totalsByMonth: Record<string, string>;
  total: string;
}

/** Sums an area's month buckets into year buckets ('YYYY' → number). */
function yearTotals(totalsByMonth: Record<string, string>): Record<string, number> {
  const byYear: Record<string, number> = {};
  for (const [month, amount] of Object.entries(totalsByMonth)) {
    const year = month.slice(0, 4);
    byYear[year] = (byYear[year] ?? 0) + Number(amount);
  }
  return byYear;
}

interface TotalsTableProps {
  heading: string;
  columns: string[];
  formatColumn: (col: string) => string;
  /** Per-area row values keyed by column. */
  rows: { key: string; label: string; byColumn: Record<string, number>; total: number }[];
  columnTotals: Record<string, number>;
  grandTotal: number;
}

function TotalsTable({
  heading,
  columns,
  formatColumn,
  rows,
  columnTotals,
  grandTotal,
}: TotalsTableProps): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left text-sm">
        <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="sticky left-0 z-10 bg-white px-4 py-2 font-medium">{heading}</th>
            {columns.map((c) => (
              <th key={c} className="whitespace-nowrap px-3 py-2 text-right font-medium">
                {formatColumn(c)}
              </th>
            ))}
            <th className="whitespace-nowrap px-4 py-2 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => (
            <tr key={row.key}>
              <td className="sticky left-0 z-10 bg-white px-4 py-2 font-medium text-slate-700">
                {row.label}
              </td>
              {columns.map((c) => (
                <td key={c} className="whitespace-nowrap px-3 py-2 text-right text-slate-600">
                  {row.byColumn[c] ? money(row.byColumn[c]) : '—'}
                </td>
              ))}
              <td className="whitespace-nowrap px-4 py-2 text-right font-semibold text-slate-900">
                {money(row.total)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t-2 border-slate-200 text-sm font-semibold text-slate-900">
          <tr>
            <td className="sticky left-0 z-10 bg-white px-4 py-2">Total</td>
            {columns.map((c) => (
              <td key={c} className="whitespace-nowrap px-3 py-2 text-right">
                {columnTotals[c] ? money(columnTotals[c]) : '—'}
              </td>
            ))}
            <td className="whitespace-nowrap px-4 py-2 text-right">{money(grandTotal, true)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

interface ForecastSummaryGridsProps {
  areas: ForecastSummaryArea[];
}

/**
 * Two combined totals grids at the top of the forecast page: one by month, one by year.
 * Each sums the per-month numbers across the supplied forecast areas (Opportunities,
 * Agreements, Projects), with a per-area row plus a grand-total row and column totals.
 */
export function ForecastSummaryGrids({ areas }: ForecastSummaryGridsProps): JSX.Element | null {
  if (areas.length === 0) return null;

  // Union of months across all areas, sorted.
  const monthSet = new Set<string>();
  for (const area of areas) {
    for (const month of Object.keys(area.totalsByMonth)) monthSet.add(month);
  }
  const months = [...monthSet].sort();

  // Per-area year buckets + the union of years.
  const areaYears = areas.map((a) => ({
    key: a.key,
    label: a.label,
    byYear: yearTotals(a.totalsByMonth),
  }));
  const yearSet = new Set<string>();
  for (const a of areaYears) for (const y of Object.keys(a.byYear)) yearSet.add(y);
  const years = [...yearSet].sort();

  // Month grid rows + totals.
  const monthRows = areas.map((a) => {
    const byColumn: Record<string, number> = {};
    for (const [month, amount] of Object.entries(a.totalsByMonth)) byColumn[month] = Number(amount);
    return { key: a.key, label: a.label, byColumn, total: Number(a.total) };
  });
  const monthColumnTotals: Record<string, number> = {};
  for (const m of months) {
    monthColumnTotals[m] = monthRows.reduce((sum, r) => sum + (r.byColumn[m] ?? 0), 0);
  }
  const grandTotal = monthRows.reduce((sum, r) => sum + r.total, 0);

  // Year grid rows + totals.
  const yearRows = areaYears.map((a) => ({
    key: a.key,
    label: a.label,
    byColumn: a.byYear,
    total: Object.values(a.byYear).reduce((s, v) => s + v, 0),
  }));
  const yearColumnTotals: Record<string, number> = {};
  for (const y of years) {
    yearColumnTotals[y] = yearRows.reduce((sum, r) => sum + (r.byColumn[y] ?? 0), 0);
  }

  return (
    <div className="mb-4 space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <span className="text-sm font-semibold text-slate-900">
            Total forecast by year
            <span className="ml-2 text-xs font-normal text-slate-500">
              Opportunities + Agreements + Projects
            </span>
          </span>
          <span className="text-sm font-semibold text-slate-900">{money(grandTotal, true)}</span>
        </div>
        <div className="border-t border-slate-200">
          <TotalsTable
            heading="Source"
            columns={years}
            formatColumn={(y) => y}
            rows={yearRows}
            columnTotals={yearColumnTotals}
            grandTotal={grandTotal}
          />
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <span className="text-sm font-semibold text-slate-900">
            Total forecast by month
            <span className="ml-2 text-xs font-normal text-slate-500">
              Opportunities + Agreements + Projects
            </span>
          </span>
          <span className="text-sm font-semibold text-slate-900">{money(grandTotal, true)}</span>
        </div>
        <div className="border-t border-slate-200">
          <TotalsTable
            heading="Source"
            columns={months}
            formatColumn={formatForecastMonth}
            rows={monthRows}
            columnTotals={monthColumnTotals}
            grandTotal={grandTotal}
          />
        </div>
      </div>
    </div>
  );
}
